import { useConfigStore } from '@/stores/ConfigStore'
import { useConnectionStore } from '@/stores/ConnectionStore'
import { useCardStore } from '@/stores/CardStore'
import { 
  REQUIRED_FIELDS_FOR_LIST_TO_COLLX, 
  CONDITION, 
  TCG_CONDITION, 
  VIEW_GRADER_WEBSITE_LOOKUP_LINKS,
  EBAY_TYPES_BY_CATEGORIES,
} from '@/constants'

export const getDefaultSelectedCardId = () => {
  const cardStore = useCardStore()
  
  return cardStore.batchCardsBasedOnStatus.draftBatchCards.length ? cardStore.batchCardsBasedOnStatus.draftBatchCards[0].id : cardStore.batchCardsBasedOnStatus.readyToListBatchCards.length ? cardStore.batchCardsBasedOnStatus.readyToListBatchCards[0].id : null
}

export const getNextCardId = () => {
  const cardStore = useCardStore()
  const currentCardIdIndex = Object.keys(cardStore.batchCardsBasedOnStatus.draftBatchCards).findIndex(item => cardStore.batchCardsBasedOnStatus.draftBatchCards[item].id === cardStore.selectedCardId)
  return cardStore.batchCardsBasedOnStatus.draftBatchCards.length >= currentCardIdIndex + 1 
    ? currentCardIdIndex === cardStore.batchCardsBasedOnStatus.draftBatchCards.length - 1 
      ? cardStore.batchCardsBasedOnStatus.draftBatchCards[0]?.id || cardStore.batchCardsBasedOnStatus.readyToListBatchCards[0]?.id
      : cardStore.batchCardsBasedOnStatus.draftBatchCards[currentCardIdIndex + 1].id 
    : cardStore.selectedCardId
}

export const omitQueryFilters = (fieldNames) => {
  const cardStore = useCardStore()
  
  return Object.keys(cardStore.itemFilters).reduce((acc, item) => {
    if (!fieldNames.includes(item)) {
      acc[item] = cardStore.itemFilters[item]
    }

    return acc
  }, {})
}

export const toggleAllRows = ({ params }) => {
  const cardStore = useCardStore()

  const haveUnselectedItems = !!cardStore.cards.find(element => element.select === false)

  if (haveUnselectedItems) {
    params.api.refreshCells({
      force: true,
      suppressFlash: true,
    })
    cardStore.cards = cardStore.cards.map((element, index) => {
      if (!element.select) {
        element.select = true
        if (cardStore.selectedRanges) {
          cardStore.selectedRanges.push({ 
            startIndex: index, 
            endIndex: index, 
            id: element.id,
            statusDisabled: element.status === 'draft' && element.has_errors,
            graded: element.graded 
          })

          cardStore.selectedCards[element.id] = element
        } else {
          cardStore.selectedRanges = [{ 
            startIndex: index, 
            endIndex: index, 
            id: element.id,
            statusDisabled: element.status === 'draft' && element.has_errors,
            graded: element.graded 
          }] 

          cardStore.selectedCards[element.id] = element    
        }
      }
      return element
    })
  } else {
    params.api.refreshCells({
      force: true,
      suppressFlash: true,
    })

    cardStore.cards.forEach(element => {
      if (element.select) {
        element.select = false
        cardStore.selectedRanges = null
        cardStore.selectedCards = {}
        cardStore.isBulkEditing = false
      }
    })
  }
}

export const handleOnRowClicked = (params, setFocus = false) => {
  const cardStore = useCardStore()
  
  cardStore.pinnedCardData.isMatchOnly = false
  cardStore.selectedCardId = params.data.id
  cardStore.selectedCardData = cardStore.cards.find(item => item.id === Number(cardStore.selectedCardId)) || {}
  
  // Remove focus from previously focused rows
  const focusedRow = document.querySelectorAll('.ag-row-focus.ag-row-no-focus')
  if (focusedRow.length) {
    focusedRow[0].classList.remove('ag-row-focus')
    focusedRow[1] && focusedRow[1].classList.remove('ag-row-focus')
  }

  // Add focus to the clicked row
  if (params.node && params.node.rowElement) {
    params.node.rowElement.classList.add('ag-row-focus')
  }

  if (params.data.select) {
    params.node.setSelected(true)
  }

  if (setFocus) {
    const selectedCell = document.querySelectorAll('.ag-cell-range-selected')

    if (selectedCell[0]) {
      selectedCell[0].classList.remove('ag-cell-range-selected')
      selectedCell[0].classList.remove('ag-cell-range-selected-1')
    }
    params.api.setFocusedCell(params.rowIndex, 'database_match')
  }
}

export const sortSubsetSuggestions = (a, b) => {
  const subsetA = a.set.subset !== null ? a.set.subset : a.set.name
  const subsetB = b.set.subset !== null ? b.set.subset : b.set.name

  if (subsetA < subsetB) return -1
  if (subsetA > subsetB) return 1
  return 0
}

export const getCardNameForPricing = (card) => {
  let name = ''
  const { card_number, set, player, subset, variation, grader, grade_number, serial_number } = card

  if (set) {
    name = `${name} ${set}`
  } 

  if (card_number) {
    name = `${name} #${card_number}`
  }

  if (player) {
    name = `${name} ${player}`
  } 

  if (subset) {
    name = `${name} ${subset}`
  } 

  if (variation) {
    name = `${name} ${variation}`
  } 

  if (grader) {
    name = `${name} ${grader}`
  } 

  if (grade_number) {
    name = `${name} ${grade_number}`
  } 

  if (serial_number) {
    name = `${name} ${serial_number}`
  }

  return encodeURIComponent(name)
}

const addDigitsToSku = (skuNumber, skuWidth) => {
  let skuString = String(skuNumber)
  while (skuString.length < skuWidth) {
    skuString = '0' + skuString
  }
  return skuString
}

export const preparePreviewSku = (skuNumber, skuWidth, skuPrefix) => {
  const skuString = addDigitsToSku(skuNumber, skuWidth)
  return skuPrefix ? `${skuPrefix} ${skuString}` : skuString
}

export const getTitle = (card) => {
  const selectedCard = card

  if (selectedCard?.title) {
    return selectedCard.title
  }

  if (selectedCard?.template_title) {
    return selectedCard.template_title
  }

  return ''
}

export const isItNoMatchFound = (card) => {
  const selectedCard = card

  if (!selectedCard?.title && !selectedCard.player && !selectedCard.set && !selectedCard.card_number && !selectedCard.team && !selectedCard.year) {
    return true
  }

  return false
}

export const getErrorsForListToCollX = (card, onlyErrors = false) => {
  const { errors, suggested_edits: suggestedEdits, type } = card
  const errorsList = []

  REQUIRED_FIELDS_FOR_LIST_TO_COLLX.forEach((field) => {
    if (field === 'category') {
      if (type === 'sports') {
        errors?.sport && errorsList.push(errors.sport)
        !onlyErrors && suggestedEdits?.sport && errorsList.push(suggestedEdits.sport)
      } else {
        errors?.game && errorsList.push(errors.game)
        !onlyErrors && suggestedEdits?.game && errorsList.push(suggestedEdits.game)
      }
    } else {
      if (errors && (field !== 'start_price' || (errors[field] !== 'Sale Price: On eBay, the Buy It Now price must be greater than or equal to US $0.99'))) {
        errors[field] && errorsList.push(errors[field])
        !onlyErrors && suggestedEdits[field] && errorsList.push(suggestedEdits[field])
      }
    }
  })

  const cardStore = useCardStore()
  const configStore = useConfigStore()

  const graders = configStore.getConfigField('graders') || []

  if (card.graded && !card.cert_no) {
    errorsList.push('Cert No: To list on CollX, a certificate number is required for graded cards')
  }

  if (card.graded && card.grader) {
    // check if grader from CDP grader list
    const grader = graders.find((item) => item.name === card.grader)

    if (!grader) {
      errorsList.push('Grader: To list on CollX, please select a Grader from available options')
    }

    // check if grade number from CDP grade numbers list
    if (grader && card.grade_number) {
      const gradeNumber = grader.grading_scale.find((item) => item === card.grade_number)

      if (!gradeNumber) {
        errorsList.push('Grade: To list on CollX, please select a Grade from available options')
      }
    }

    if (grader && !card.grade_number) {
      errorsList.push('Grade: To list on CollX, please select a Grade from available options')
    }
  }

  if (card.graded && !card.grader) {
    errorsList.push('Grader: To list on CollX, please select a Grader from available options')
  }

  if (!card.graded && card.condition) {
    if (cardStore.selectedCardData.type === 'tcg' && !TCG_CONDITION.find(item => item.name === card.condition)) {
      errorsList.push('Condition: To list on CollX, please select Condition from available options')
    }

    if (cardStore.selectedCardData.type === 'sports' && !CONDITION.find(item => item.name === card.condition)) {
      errorsList.push('Condition: To list on CollX, please select Condition from available options')
    }
  }

  if (!card.graded && !card.condition) {
    errorsList.push('Condition: To list on CollX, please select Condition from available options')
  }

  return errorsList
}

export const preparedListOfCardsToExportOrPublish = ({ items, selectedCards = {}, isBulkEditing, selectedRanges = [] }) => {
  let items_ = [...items]

  if (isBulkEditing) {
    if (Object.keys(selectedCards).length && !selectedRanges.length) {
      items_ = Object.entries(selectedCards).map(([l, value]) => value)
    } else if (selectedRanges && selectedRanges.length) {
      items_ = selectedRanges.reduce((acc, item) => {
        const cardData = items_.find(card => card.id === item.id)

        if (cardData) acc.push(cardData)

        return acc
      }, [])
    }
  }

  const cardsIds = items_.reduce((acc, item) => {
    acc.push(item.id)

    return acc
  }, [])

  return { cardsIds, cards: items_ }
}

export const dateScheduledTemplateErrors = (connectionStore) => {
  const errors = []
  let dateScheduledTemplateError = false

  if (connectionStore.selectedTemplatePerConnection?.['ebay-direct']?.schedule_date) {
    const scheduleDate = connectionStore.selectedTemplatePerConnection['ebay-direct'].schedule_date
    const scheduleTime = connectionStore.selectedTemplatePerConnection['ebay-direct'].schedule_time
    
    // Combine date and time
    const scheduledDateTime = new Date(`${scheduleDate} ${scheduleTime}`)
    const now = new Date()
    
    // Check if scheduled date/time is in the past
    if (scheduledDateTime <= now && !connectionStore.selectedTemplatePerConnection['ebay-direct'].make_live_immediately) {
      errors.push('Please update your template to use a future start date, or set the listing to go live immediately.')
      dateScheduledTemplateError = true
    }
  }

  return { errors, dateScheduledTemplateError }
}

export const hasEbayPoliciesTemplateErrors = (connectionStore) => {
  return !connectionStore.selectedTemplatePerConnection?.['ebay-direct']?.ebay_location_id 
  || !connectionStore.selectedTemplatePerConnection?.['ebay-direct']?.ebay_fulfillment_policy_id 
  || !connectionStore.selectedTemplatePerConnection?.['ebay-direct']?.ebay_payment_policy_id 
  || !connectionStore.selectedTemplatePerConnection?.['ebay-direct']?.ebay_return_policy_id
}

export const validateIsCardsHasCategoryNotEqualToTemplate = (card, connectionStore) => {
  const cardStore = useCardStore()
  let isCardsWithoutCategoryBasedOnTemplate = false
  let isCardsHasCategoryNotEqualToTemplate = false

  if (card.type !== EBAY_TYPES_BY_CATEGORIES[connectionStore.selectedTemplatePerConnection?.[cardStore.isTryToExport ? 'ebay' : 'ebay-direct']?.ebay_category]) {
    isCardsHasCategoryNotEqualToTemplate = true
  }

  if (isCardsHasCategoryNotEqualToTemplate && card.type === EBAY_TYPES_BY_CATEGORIES[connectionStore.selectedTemplatePerConnection?.[cardStore.isTryToExport ? 'ebay' : 'ebay-direct']?.ebay_category]) {
    isCardsHasCategoryNotEqualToTemplate = false
    isCardsWithoutCategoryBasedOnTemplate = true
  }

  return { isCardsWithoutCategoryBasedOnTemplate, isCardsHasCategoryNotEqualToTemplate }
}

export const checkAreRequiredFieldsEmpty = (cards, connections) => {
  if (!connections.length) return

  const connectionStore = useConnectionStore()

  let isCollxError = false
  let isEbayError = false
  let isWhatnotError = false
  let isEbayTemplateError = false
  let isShopifyError = false
  let isDateScheduledTemplateError = false
  let cardsWithoutCategoryBasedOnTemplate = false
  let cardsHasCategoryNotEqualToTemplate = false

  for(let i = 0; i < cards.length; i++) {
    const errors = getErrorsForListToCollX(cards[i])

    if (!isEbayTemplateError) {
      isEbayTemplateError = hasEbayPoliciesTemplateErrors(connectionStore)
    }

    if (errors.length) {
      isCollxError = true
    }

    if (cards[i].has_errors) {
      isEbayError = true
      isWhatnotError = true
      isShopifyError = true
    }

    const { dateScheduledTemplateError } = dateScheduledTemplateErrors(connectionStore)
    const { isCardsWithoutCategoryBasedOnTemplate, isCardsHasCategoryNotEqualToTemplate } = validateIsCardsHasCategoryNotEqualToTemplate(cards[i], connectionStore)

    if (isCardsWithoutCategoryBasedOnTemplate) {
      cardsWithoutCategoryBasedOnTemplate = true
    }

    if (isCardsHasCategoryNotEqualToTemplate) {
      cardsHasCategoryNotEqualToTemplate = true
    }

    if (dateScheduledTemplateError) {
      isDateScheduledTemplateError = true
    }

    if (isCollxError && isEbayError) break
  }

  return {
    collx: isCollxError,
    ebay: isEbayError,
    whatnot: isWhatnotError,
    ebayTemplate: isEbayTemplateError,
    shopify: isShopifyError,
    dateScheduledTemplateError: isDateScheduledTemplateError,
    cardsWithoutCategoryBasedOnTemplate,
    cardsHasCategoryNotEqualToTemplate
  }
}

export const getViewGraderWebsiteLookupLink = (grader, certNo) => {
  return `${VIEW_GRADER_WEBSITE_LOOKUP_LINKS[grader]}${certNo}`
}

 // Function to check if a cell contains a custom component that needs manual focus handling
 export const  isCustomComponentCell = (cellPosition, params) => {
  const column = cellPosition.column
  const rowNode = params.api.getDisplayedRowAtIndex(cellPosition.rowIndex)
  
  // Check based on column ID, field, or other properties identifying custom components
  return column.getColId() === 'subset' || column.getColId() === 'attributes'  // Update this ID as needed
}

// Function to manually focus the custom component in the cell
export const focusCustomComponent = (cellPosition, params) => {
  const cell = params.api.getCellRendererInstances({
    rowNodes: [params.api.getDisplayedRowAtIndex(cellPosition.rowIndex)],
    columns: [cellPosition.column],
  })[0]


  if (cell && cellPosition.column.colId === 'subset') {
    const element = cell.params?.eParentOfValue
    const inputElements = element.getElementsByTagName('input')
    const input = inputElements && inputElements[0]
    
    if (element && (typeof element.focus === 'function' || typeof input.focus === 'function')) {
      input ? input.focus() : element.focus() // Set focus to the custom component's element
    }

    if (element && (typeof element.click === 'function' || typeof input.click === 'function')) {
      input ? input.click() : element.click() // Set focus to the custom component's element

      if (!input) {
        const clickableElements = element.getElementsByTagName('section')
        const clickableEl = clickableElements[1]

        clickableEl.click()
      }
      
    } else {
      // If `click()` isn't directly available, use a custom click event
      const clickEvent = new MouseEvent('click', { bubbles: true, cancelable: true })
      element.dispatchEvent(clickEvent)
    }
  // } else if (cell && cellPosition.column.colId === 'attributes') {
  //   const element = cell.params?.eParentOfValue
  //   console.log('!!!!!!!!!!!! element', cell.params, element)

  //   if (element && typeof element.click === 'function') {
  //     const clickableElements = element.getElementsByTagName('section')
  //     const clickableEl = clickableElements[0]
  //     console.log('>> click', element)

  //     clickableEl.click()
      
  //   }
  // }
  }
}

export const getDisplayColumns = (key, userId) => {
  const savedColumns = localStorage.getItem(key)
  if (savedColumns) {
    const parsedColumns = JSON.parse(savedColumns)
    return parsedColumns[userId]
  }

  return []
}

export const valueSectionStyleForBulkInputs = ({selectedAttribute, valueState, fields}) => {
  let paddingLeft = 0
  if (
    !['status', 'start_price', 'category', 'sport', 'year', 'attributes', 'graded', 'grader', 'grade', 'purchase_price'].includes(selectedAttribute?.field)
  ) {
    if (
      (fields[selectedAttribute.field]
      && valueState === 'entered')
      || selectedAttribute.field === 'title'
      || selectedAttribute.field === 'description'
    ) {
      paddingLeft = 17
    }
  }

  if (
    selectedAttribute.field === 'year'
    && fields?.year
    && valueState === 'entered'
  ) {
    paddingLeft = 8
  }

  if (
    selectedAttribute.field === 'attributes'
    && valueState
  ) {
    paddingLeft = 10
  }

  return {
    'padding-left': paddingLeft + 'px'
  }
}